'use client';

import { useId } from 'react';
import { useTranslations } from 'next-intl';
import { FieldSet, InlineFeedback, Legend, VisuallyHidden } from '@zillow/constellation-10';
import { CodeInput } from '@/src/components/CodeInput';
import { feedback } from './otpCodeEntry.styles';

export type OtpCodeEntryProps = {
  code: string;
  onCodeChange: (code: string) => void;
  /** Shuts the boxes: a verify in flight, or a code the server has retired. */
  disabled: boolean;
  isVerifying: boolean;
  /** Current failure copy, from a send or a verify; empty when there is none. */
  errorMessage: string;
  /** Show that a new code is on its way; an error takes precedence. */
  resendConfirmed: boolean;
};

/**
 * The code boxes with their feedback and in-flight announcement, for rendering inside the
 * caller's form. Copy comes from the shared `auth.otp*` keys; what the code is for is the
 * caller's description to write.
 */
export function OtpCodeEntry({
  code,
  onCodeChange,
  disabled,
  isVerifying,
  errorMessage,
  resendConfirmed,
}: OtpCodeEntryProps) {
  const t = useTranslations('auth');
  const feedbackId = useId();
  const idPrefix = useId();

  return (
    <>
      <FieldSet
        legend={<Legend>{t('otpCodeLegend')}</Legend>}
        error={Boolean(errorMessage)}
        // FieldSet associates this with every nested control via aria-describedby, so the
        // boxes do not need to be wired up by hand. Always rendered, empty included: a
        // live region that appears at the same moment its text does is not reliably
        // announced.
        description={
          <span id={feedbackId} aria-live="polite" aria-atomic="true" className={feedback}>
            {/* Compound form so the label can be sized: the shorthand renders body (16px),
                which sits oddly next to the 14px legend above. Set via `css` rather than the
                `textStyle` prop — the label merges `css` after its own recipe, so the prop
                alone loses to the recipe's body. */}
            {errorMessage && (
              <InlineFeedback.Root tone="critical" role="alert">
                <InlineFeedback.Icon />
                <InlineFeedback.Label css={{ textStyle: 'body-sm' }}>{errorMessage}</InlineFeedback.Label>
              </InlineFeedback.Root>
            )}
            {!errorMessage && resendConfirmed && (
              <InlineFeedback.Root tone="success">
                <InlineFeedback.Icon />
                <InlineFeedback.Label css={{ textStyle: 'body-sm' }}>{t('otpResendConfirmation')}</InlineFeedback.Label>
              </InlineFeedback.Root>
            )}
          </span>
        }
      >
        <CodeInput
          idPrefix={idPrefix}
          digitLabel={(position, total) => t('otpDigitLabel', { position, total })}
          value={code}
          onChange={onCodeChange}
          disabled={disabled}
          error={Boolean(errorMessage)}
        />
      </FieldSet>

      {/* Constellation renders the label transparent under `loading` and overlays the
          spinner, so the in-flight state has to be announced separately — swapping the
          label would change the accessible name but show nobody anything. Always
          rendered, empty included: a live region that appears at the same moment its text
          does is not reliably announced. */}
      <VisuallyHidden role="status" aria-live="polite">
        {isVerifying ? t('otpVerifying') : ''}
      </VisuallyHidden>
    </>
  );
}
